import { useState } from "react";
import "../data/style/keyframes.css";
import Stack from "@mui/material/Stack";
import * as routes from "../data/links/routes";
import { useAuth } from "../context/AuthContext";
import { Link, useParams } from "react-router-dom";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import PasswordField from "../components/Inputs/PasswordField";
import RequestButton from "../components/buttons/RequestButton";
import { FormContainer, StyledLink } from "../data/style/Style";
import { handleReCaptchaVerify, captcha } from "../utils/helpers";
import { useNotifications } from "../context/NotificationContext";
import { DescriptionText, TwoTonedHeader } from "../components/theme/texts";

const ChangePassword = () => {
  const { otp } = useParams();
  let { ResetPassword, activeRequest, setActiveRequest } = useAuth();
  const { executeV3 } = useGoogleReCaptcha();
  const { enableNotification } = useNotifications();
  const [pw, setPw] = useState("");
  const [focusPw, setFocusPw] = useState(false);
  const [focusCf, setFocusCf] = useState(false);
  const [confirm, setConfirm] = useState("");

  const reqs = [
    { met: pw.length >= 10, text: "At least 10 characters" },
    { met: pw.length <= 20, text: "At most 20 characters" },
  ];

  const canSubmit = pw.length >= 10 && pw.length <= 20 && pw === confirm;
  let handlePasswordReset = async () => {
    const token = await executeV3();
    await handleReCaptchaVerify(
      token,
      "reset_password",
      document.cookie.split("=")[1],
    );
    if (captcha.success) {
      setActiveRequest(true);
      ResetPassword.mutate({ otp, pass: pw, c_pass: confirm });
    } else {
      enableNotification("Something went wrong. Please try again.", "error");
    }
  };
  return (
    <Stack gap={2} width={"50%"} alignItems={"center"}>
      <TwoTonedHeader
        mainText={"Reset your"}
        subText={"password"}
        fontSize={"clamp(36px,5.5vw,60px)"}
      />
      <DescriptionText style={{ fontSize: "clamp(14px,1.5vw,17px)" }}>
        Choose a strong new password for your account.
      </DescriptionText>
      <FormContainer>
        <div
          style={{
            height: 2,
            background:
              "linear-gradient(90deg,#f97316,#ea580c,rgba(249,115,22,.04))",
          }}
        />
        <Stack width={"90%"} p={5} gap={4} alignSelf={"center"}>
          <PasswordField
            id="gp-pw"
            label="New Password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onFocus={() => setFocusPw(true)}
            onBlur={() => setFocusPw(false)}
            focused={focusPw}
            // error={true}
            showToggle
          />
          {/* Requirements */}
          {(focusPw || pw) && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6px 16px",
                padding: "12px 14px",
                borderRadius: 12,
                background: "#0d0d0d",
                border: "1px solid #1e1e1e",
              }}
            >
              {reqs.map((r, i) => (
                <Req key={i} met={r.met} text={r.text} />
              ))}
            </div>
          )}
          <PasswordField
            id="gp-cf"
            label="Confirm Password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onFocus={() => setFocusCf(true)}
            onBlur={() => setFocusCf(false)}
            focused={focusCf}
            // error={confirmError}
            showToggle
          />
          {/* Match indicator */}
          {confirm && <MatchIndicator pw={pw} confirm={confirm} />}
          <RequestButton
            onClick={handlePasswordReset}
            activeRequest={activeRequest}
            disabled={activeRequest || !canSubmit}
            text={"Reset Password"}
            loadingText={"Updating Password..."}
          />
          <div
            style={{
              textAlign: "center",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              color: "#555",
            }}
          >
            Already know your password?&nbsp;&nbsp;
            <Link style={{ textDecoration: "none" }} to={routes.LOGIN}>
              <StyledLink>Log in →</StyledLink>
            </Link>
          </div>
        </Stack>
      </FormContainer>
    </Stack>
  );
};
export default ChangePassword;

// ─────────────────────────────────────────
// Match indicator
// ─────────────────────────────────────────
function MatchIndicator({ pw, confirm }) {
  if (!confirm) return null;
  const match = pw === confirm;
  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      gap={1}
      sx={{
        fontFamily: "DM Sans, sans-serif",
        fontSize: 12,
        color: match ? "#22d47b" : "#ff3b5c",
      }}
    >
      <span>{match ? "✓" : "✗"}</span>
      {match ? "Passwords match" : "Passwords do not match"}
    </Stack>
  );
}

// ─────────────────────────────────────────
// Password requirement row
// ─────────────────────────────────────────
function Req({ met, text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          flexShrink: 0,
          background: met ? "rgba(34,212,123,.15)" : "rgba(255,255,255,.04)",
          border: `1px solid ${met ? "rgba(34,212,123,.4)" : "#333"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all .2s",
        }}
      >
        {met && (
          <svg width="9" height="9" viewBox="0 0 9 9">
            <polyline
              points="1.5,4.5 3.5,6.5 7.5,2.5"
              fill="none"
              className="check"
              stroke="#22d47b"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="10"
              strokeDashoffset="10"
            />
          </svg>
        )}
      </div>
      <span
        style={{
          fontFamily: "DM Sans, sans-serif",
          fontSize: 12,
          color: met ? "#b0b0b0" : "#444",
          transition: "color .2s",
        }}
      >
        {text}
      </span>
    </div>
  );
}
